import { z } from 'zod';
import { PlatformSchema, type Platform } from './platform.js';

/**
 * Resposta 409 do attach (#40 #130) — contrato compartilhado entre
 * `ChatRoomDO` (caminho real) e `chat-gateway/handler` (short-circuit via
 * `GET /state`). Centralizado aqui pra evitar drift entre os dois consumers:
 * qualquer mudança no critério de conflict ou no shape da resposta acontece
 * num único lugar.
 */

export const AttachConflictDetailSchema = z.enum([
  'no_primary',
  'same_platform',
  'platform_occupied',
]);
export type AttachConflictDetail = z.infer<typeof AttachConflictDetailSchema>;

export const AttachOccupiedBySchema = z.object({
  platform: PlatformSchema,
  externalId: z.string().min(1),
});
export type AttachOccupiedBy = z.infer<typeof AttachOccupiedBySchema>;

export const AttachConflictResponseSchema = z.object({
  ok: z.literal(false),
  errorCode: z.literal('attach_conflict'),
  detail: AttachConflictDetailSchema,
  occupiedBy: AttachOccupiedBySchema.nullable(),
});
export type AttachConflictResponse = z.infer<typeof AttachConflictResponseSchema>;

/** Parts internas do conflict (sem o envelope `ok`/`errorCode`). */
export interface AttachConflict {
  detail: AttachConflictDetail;
  occupiedBy: AttachOccupiedBy | null;
}

interface RefSummary {
  platform: Platform;
  externalId: string;
}

/**
 * Predicado puro do conflito de attach. Retorna `null` quando a anexação é
 * legítima (incluindo o caso idempotente "mesma live já anexada"); caso
 * contrário retorna o `{ detail, occupiedBy }` que descreve o conflito.
 *
 * Critérios (mesma ordem do `ChatRoomDO.handleAttachUpgrade`):
 *   1. sem primary → `no_primary` (occupiedBy null).
 *   2. primary.platform === attachPlatform → `same_platform` (occupiedBy = primary).
 *   3. attached.platform === attachPlatform com externalId distinto →
 *      `platform_occupied` (occupiedBy = attached). Mesmo externalId é
 *      idempotência: não-conflito.
 */
export function evalAttachConflict(input: {
  primary: RefSummary | null;
  attached: RefSummary | null;
  attachPlatform: Platform;
  attachExternalId: string;
}): AttachConflict | null {
  const { primary, attached, attachPlatform, attachExternalId } = input;

  if (!primary) {
    return { detail: 'no_primary', occupiedBy: null };
  }
  if (primary.platform === attachPlatform) {
    return {
      detail: 'same_platform',
      occupiedBy: { platform: primary.platform, externalId: primary.externalId },
    };
  }
  if (
    attached &&
    attached.platform === attachPlatform &&
    attached.externalId !== attachExternalId
  ) {
    return {
      detail: 'platform_occupied',
      occupiedBy: { platform: attached.platform, externalId: attached.externalId },
    };
  }
  return null;
}

/** Monta o envelope completo da resposta 409 a partir do conflict puro. */
export function buildAttachConflictResponse(conflict: AttachConflict): AttachConflictResponse {
  return {
    ok: false,
    errorCode: 'attach_conflict',
    detail: conflict.detail,
    occupiedBy: conflict.occupiedBy,
  };
}
